import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Upload, 
  Users, 
  BarChart3, 
  Shield, 
  FileText,
  Video,
  Bell,
  ArrowRight,
  Check
} from 'lucide-react';

const features = [
  {
    icon: Upload,
    title: 'Smart Document Upload',
    description: 'Upload PDFs or capture photos of your medical reports. Our AI extracts and structures the data automatically.'
  },
  {
    icon: BarChart3,
    title: 'Health Trends & Analytics',
    description: 'Track your health metrics over time with interactive charts and AI-powered insights.'
  },
  {
    icon: Users,
    title: 'Family Management',
    description: 'Manage health records for your entire family under one account with secure access controls.'
  },
  {
    icon: Video,
    title: 'Video Consultations',
    description: 'Schedule and conduct group video consultations with your doctor and family members.'
  },
  {
    icon: FileText,
    title: 'E-Prescriptions & Certificates',
    description: 'Generate and manage digital prescriptions and medical certificates with ease.'
  },
  {
    icon: Bell,
    title: 'Health Reminders',
    description: 'Never miss a quarterly checkup or medication reminder with our smart notification system.'
  }
];

const benefits = [
  'AI-powered document analysis',
  'Secure family health records',
  'Real-time health insights',
  'HIPAA compliant storage',
  'Easy export & sharing',
  '24/7 access to records'
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50 px-4 sm:px-6 lg:px-12 py-4 md:py-5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/lyfsync-logo.png" alt="LYFSYNC logo" className="w-14 h-14 object-contain" data-testid="landing-brand-logo" />
            <div>
              <span className="font-bold text-2xl text-slate-900 block" style={{ fontFamily: 'Manrope, sans-serif' }}>
                LYFSYNC
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-slate-400">Health at your fingertips</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <Link to="/provider/login" data-testid="nav-provider-login-link">
              <Button variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="nav-provider-login-btn">
                Provider Login
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="nav-login-btn">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg px-6" data-testid="nav-register-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-medium">
                <Shield className="w-4 h-4" />
                <span>Secure & HIPAA Compliant</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Your Family's Health,{' '}
                <span className="text-blue-900">Intelligently Managed</span>
              </h1>
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
                Upload medical reports, get AI-powered insights, track health trends, and manage your entire family's healthcare journey in one secure platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg px-8 py-6 text-lg font-medium shadow-lg shadow-blue-900/20 btn-active w-full sm:w-auto"
                    data-testid="hero-get-started-btn"
                  >
                    Start Free Today
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="rounded-lg px-8 py-6 text-lg font-medium border-slate-300 w-full sm:w-auto"
                    data-testid="hero-signin-btn"
                  >
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-blue-50 to-green-50 rounded-3xl" />
                <img
                  src="https://images.pexels.com/photos/8376221/pexels-photo-8376221.jpeg"
                  alt="Doctor consulting with patient"
                  className="relative rounded-2xl shadow-xl w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-8 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2 text-slate-600">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Everything You Need for Health Management
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From document analysis to family coordination, we've got all your healthcare management needs covered.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="p-8 bg-slate-50 rounded-2xl border border-slate-200 hover:border-blue-200 transition-colors card-hover animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-blue-900" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-blue-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Take Control of Your Family's Health Today
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of families who trust LYFSYNC to manage their medical records and health insights.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-blue-900 hover:bg-blue-50 rounded-lg px-8 py-6 text-lg font-medium shadow-lg btn-active"
              data-testid="cta-get-started-btn"
            >
              Create Free Account
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 md:px-8 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <img src="/lyfsync-logo.png" alt="LYFSYNC logo" className="w-5 h-5 object-contain" data-testid="footer-brand-logo" />
            </div>
            <span className="font-semibold text-white">LYFSYNC</span>
          </div>
          <p className="text-sm">© 2024 LYFSYNC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
